import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatError } from "@/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ListChecks, Search, Filter, ChevronDown, AlertTriangle, CheckCircle2 } from "lucide-react";

const STATUS_FILTERS = [
    { key: "",          label: "Toate" },
    { key: "active",    label: "Active" },
    { key: "overdue",   label: "Întârziate" },
    { key: "blocked",   label: "Blocate" },
    { key: "completed", label: "Finalizate" },
];

export default function AdminTasksPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isAdmin = ["super_admin", "admin"].includes(user?.role);

    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [workspaceId, setWorkspaceId] = useState("");
    const [teamId, setTeamId] = useState("");
    const [assigneeId, setAssigneeId] = useState("");
    const [workspaces, setWorkspaces] = useState([]);
    const [teams, setTeams] = useState([]);
    const [users, setUsers] = useState([]);

    useEffect(() => {
        if (!isAdmin) return;
        API.get("/workspaces").then(r => setWorkspaces(r.data)).catch(() => {});
        API.get("/teams").then(r => setTeams(r.data)).catch(() => {});
        API.get("/users").then(r => setUsers(r.data)).catch(() => {});
    }, [isAdmin]);

    const fetchTasks = () => {
        if (!isAdmin) return;
        setLoading(true);
        const params = new URLSearchParams();
        if (workspaceId) params.set("workspace_id", workspaceId);
        if (teamId) params.set("team_id", teamId);
        if (assigneeId) params.set("assignee_id", assigneeId);
        if (status) params.set("status", status);
        if (search.trim()) params.set("search", search.trim());
        API.get(`/admin/tasks/all?${params.toString()}`)
            .then(r => setTasks(r.data))
            .catch(err => toast.error(formatError(err)))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchTasks(); /* eslint-disable-next-line */ }, [isAdmin, workspaceId, teamId, assigneeId, status]);

    useEffect(() => {
        const t = setTimeout(fetchTasks, 350);
        return () => clearTimeout(t);
    // eslint-disable-next-line
    }, [search]);

    const filteredTeams = useMemo(() => {
        if (!workspaceId) return teams;
        return teams.filter(t => t.workspace_id === workspaceId);
    }, [teams, workspaceId]);

    if (!isAdmin) {
        return <div className="p-6 text-sm text-[#9CA3AF]">Acces interzis.</div>;
    }

    const now = new Date();
    const progressOf = (task) => {
        if (!task.checklist_count) return null;
        return Math.round(100 * (task.checklist_completed || 0) / task.checklist_count);
    };

    return (
        <div className="p-6 max-w-[1400px] mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-md bg-[#002FA7] text-white flex items-center justify-center">
                    <ListChecks size={20} />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-[#111827] dark:text-[#F3F4F6]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Toate taskurile</h1>
                    <p className="text-sm text-[#9CA3AF] dark:text-[#7E858E]">Vizualizare agregată a tuturor taskurilor din organizație</p>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
                <div className="relative flex-1 min-w-[220px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                    <input value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Caută după titlu..."
                        data-testid="admin-tasks-search"
                        className="w-full pl-8 pr-3 py-2 border border-[#E4E4E7] rounded-md text-sm" />
                </div>
                <select value={workspaceId} onChange={e => { setWorkspaceId(e.target.value); setTeamId(""); }}
                    data-testid="admin-tasks-workspace-filter"
                    className="px-3 py-2 border border-[#E4E4E7] rounded-md text-sm">
                    <option value="">Toate workspace-urile</option>
                    {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
                <select value={teamId} onChange={e => setTeamId(e.target.value)}
                    data-testid="admin-tasks-team-filter"
                    className="px-3 py-2 border border-[#E4E4E7] rounded-md text-sm">
                    <option value="">Toate echipele</option>
                    {filteredTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)}
                    data-testid="admin-tasks-assignee-filter"
                    className="px-3 py-2 border border-[#E4E4E7] rounded-md text-sm">
                    <option value="">Toți membrii</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                </select>
                <div className="flex gap-1 bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md p-0.5">
                    {STATUS_FILTERS.map(f => (
                        <button key={f.key} onClick={() => setStatus(f.key)}
                            data-testid={`admin-tasks-status-${f.key || "all"}`}
                            className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${status === f.key ? "bg-[#002FA7] text-white" : "text-[#4B5563] hover:bg-[#F1F3F5]"}`}>
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-lg overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-[#F8F9FA] dark:bg-[#0B0D10] border-b border-[#E4E4E7] dark:border-[#252B33] text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">
                    <div className="col-span-4">Task</div>
                    <div className="col-span-2">Board</div>
                    <div className="col-span-2">Echipă</div>
                    <div className="col-span-1">Responsabil</div>
                    <div className="col-span-1">Deadline</div>
                    <div className="col-span-2">Progres</div>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-sm text-[#9CA3AF]">Se încarcă...</div>
                ) : tasks.length === 0 ? (
                    <div className="p-12 text-center text-sm text-[#9CA3AF]" data-testid="admin-tasks-empty">Niciun task</div>
                ) : (
                    <div data-testid="admin-tasks-list">
                    {tasks.map(task => {
                        const overdue = task.due_date && new Date(task.due_date) < now && !task.completed_at;
                        const progress = progressOf(task);
                        const completed = !!task.completed_at;
                        return (
                            <button key={task.id}
                                onClick={() => task.board_id && navigate(`/boards/${task.board_id}?task=${task.id}`)}
                                data-testid={`admin-task-row-${task.id}`}
                                className="w-full grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#E4E4E7] dark:border-[#252B33] hover:bg-[#F8F9FA] dark:hover:bg-[#1C222A] text-left last:border-b-0">
                                <div className="col-span-4 flex items-center gap-2 min-w-0">
                                    {task.is_blocked && <AlertTriangle size={13} className="text-red-500 flex-shrink-0" />}
                                    {completed && <CheckCircle2 size={13} className="text-green-500 flex-shrink-0" />}
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-[#111827] dark:text-[#F3F4F6] truncate">{task.title}</p>
                                        <p className="text-[10px] text-[#9CA3AF] truncate">#{task.task_number} • {task.column_name}</p>
                                    </div>
                                </div>
                                <div className="col-span-2 text-xs text-[#4B5563] dark:text-[#B5BAC1] truncate self-center">{task.board_name || "—"}</div>
                                <div className="col-span-2 text-xs text-[#4B5563] dark:text-[#B5BAC1] truncate self-center">{task.team_name || "—"}</div>
                                <div className="col-span-1 self-center">
                                    {task.assignee ? (
                                        <div className="flex items-center gap-1.5 min-w-0">
                                            <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: task.assignee.avatar || "#002FA7" }}>
                                                <span className="text-white text-[9px] font-bold">{(task.assignee.nickname || task.assignee.name || "?")[0]?.toUpperCase()}</span>
                                            </div>
                                            <span className="text-xs text-[#4B5563] dark:text-[#B5BAC1] truncate">{task.assignee.nickname || task.assignee.name}</span>
                                        </div>
                                    ) : <span className="text-xs text-[#C7C7C7]">—</span>}
                                </div>
                                <div className="col-span-1 self-center">
                                    {task.due_date ? (
                                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${overdue ? "bg-red-100 text-red-700" : completed ? "bg-gray-100 text-gray-600" : "bg-green-100 text-green-700"}`}>
                                            {new Date(task.due_date).toLocaleDateString("ro-RO", { day: "2-digit", month: "short" })}
                                        </span>
                                    ) : <span className="text-xs text-[#C7C7C7]">—</span>}
                                </div>
                                <div className="col-span-2 self-center">
                                    {progress !== null ? (
                                        <div className="flex items-center gap-2">
                                            <div className="flex-1 h-1.5 bg-[#E4E4E7] dark:bg-[#252B33] rounded-full overflow-hidden">
                                                <div className="h-full bg-[#002FA7] dark:bg-[#4F7CFF]" style={{ width: `${progress}%` }} />
                                            </div>
                                            <span className="text-[10px] font-semibold text-[#4B5563] dark:text-[#B5BAC1] w-10 text-right">{task.checklist_completed}/{task.checklist_count}</span>
                                        </div>
                                    ) : <span className="text-xs text-[#C7C7C7]">—</span>}
                                </div>
                            </button>
                        );
                    })}
                    </div>
                )}
            </div>
        </div>
    );
}
